/**
 * What "these bookmarks" means, defined once for everybody who has to mean it.
 *
 * ── Why this moved down here ─────────────────────────────────────────────────
 *
 * The filter used to live in `:client`, in `BookmarkFilter.kt`, because the only thing
 * that filtered was a browser window holding the whole collection. LPN-58 added a second
 * caller — an agent asking `search_bookmarks` over `/mcp` — and a second caller is the
 * moment a filter stops being a view concern.
 *
 * They must mean the same thing by *any of these tags* and by *newest*, and two
 * implementations of that agree only until one of them is edited. So the semantics are
 * here, in the module both sides already depend on, and there is exactly one function
 * that decides whether a bookmark is in a result set.
 *
 * `:client`'s [se.soderbjorn.lunapin.client.viewmodel.filterBookmarks] is still the name
 * the web app, Android and iOS call. It is now an adapter: it turns a saved window filter
 * into a [BookmarkQuery] and hands it here. The vocabularies over there are aliases of
 * the ones below rather than copies, so a value cannot drift and no Swift or Compose call
 * site had to change.
 *
 * ── Filtering happens in Kotlin, over rows already loaded ────────────────────
 *
 * On both sides, and deliberately. The browser holds the whole collection anyway (see
 * `BookmarkFilter.kt` for why), and the server's `/api/bookmarks` already loads all of an
 * account's rows for every list. A collection is a few thousand rows for one person, so a
 * SQL query builder would be a second dialect of these rules, written to be fast for a
 * problem nobody has.
 *
 * If it ever does get slow, the fix is to push this **behind the same signature** — same
 * [BookmarkQuery] in, same rows out — rather than to fork the semantics in the name of an
 * index.
 *
 * ── What is here and what is not ─────────────────────────────────────────────
 *
 * [filterBookmarks] answers *every* row that matches, in [BookmarkQuery.sort] order. It
 * does **not** apply [BookmarkQuery.limit] or [BookmarkQuery.offset]: a caller that wants
 * a page also wants the total, and computing the total from a list that has already been
 * cut is impossible. [pageOf] takes the cut afterwards, so "340 matched, here are 50" is
 * one traversal and two lines.
 *
 * @see se.soderbjorn.lunapin.client.viewmodel.filterBookmarks
 */
package se.soderbjorn.lunapin.clientserver

import kotlinx.serialization.Serializable

// ── The vocabularies ─────────────────────────────────────────────────────────
//
// Strings rather than enums, for the reason the client's window filter states beside the
// fields that hold them: kotlinx-serialization refuses an unknown enum value, and an
// unparseable workspace blob costs the user every tab they had open. Every `when` over one
// of these therefore has an `else` that means "the default", and a value from a later
// client — or from a confused agent — degrades to sensible rather than to nothing.

/** How [BookmarkQuery.includeTags] combine. */
object FilterMatches {
    /** A bookmark must carry every included tag. The default: filters narrow. */
    const val ALL: String = "all"

    /** Any one included tag is enough. */
    const val ANY: String = "any"

    val all: List<String> = listOf(ALL, ANY)
}

/** The order rows come back in. */
object BookmarkSorts {
    /** Newest first — the server's own order, and the one a collection is added to at. */
    const val NEWEST: String = "newest"

    /** Oldest first: the read-later pile, from the bottom. */
    const val OLDEST: String = "oldest"

    /** By title, A–Z, falling back to the URL for a bookmark that has none. */
    const val TITLE: String = "title"

    val all: List<String> = listOf(NEWEST, OLDEST, TITLE)
}

/**
 * How far back a bookmark may have been saved, as a rolling window.
 *
 * **Rolling windows, not calendar buckets** — "the last 24 hours" rather than "today".
 * That is a deliberate simplification and it buys something real: a rolling window is
 * arithmetic on two `Long`s and needs no time zone, so the filter is the same answer in
 * every zone and there is no hour of the year where it disagrees with itself.
 *
 * It is also the *only* thing a filter bar can express, and the reason
 * [BookmarkQuery.addedAfter] exists beside it: an agent asked about 2019 cannot say 2019
 * in weeks.
 */
object AddedWithins {
    const val ANY: String = "any"
    const val DAY: String = "day"
    const val WEEK: String = "week"
    const val MONTH: String = "month"
    const val YEAR: String = "year"

    val all: List<String> = listOf(ANY, DAY, WEEK, MONTH, YEAR)

    /** How long each window is, or null for [ANY], which has no bound. */
    fun millis(value: String): Long? = when (value) {
        DAY -> 24L * 60 * 60 * 1000
        WEEK -> 7L * 24 * 60 * 60 * 1000
        MONTH -> 30L * 24 * 60 * 60 * 1000
        YEAR -> 365L * 24 * 60 * 60 * 1000
        else -> null
    }
}

/**
 * Which of a bookmark's two summaries have to be there.
 *
 * Four states rather than a boolean, because [Bookmark.summary] and [Bookmark.aiSummary]
 * are two different claims — a person's note and a machine's — and "which of my bookmarks
 * did I write about myself" is a different question from "which ones did the summariser
 * get to". Folding them would answer neither.
 */
object SummaryPresences {
    /** Say nothing about summaries. The default. */
    const val ANY: String = "any"

    /** The person's own note is non-blank. */
    const val WRITTEN: String = "written"

    /** The machine's summary is non-blank. */
    const val AI: String = "ai"

    /** At least one of the two. */
    const val EITHER: String = "either"

    /** Neither — nothing has ever been said about this link. */
    const val NEITHER: String = "neither"

    val all: List<String> = listOf(ANY, WRITTEN, AI, EITHER, NEITHER)
}

/**
 * Whether a bookmark carries tags the model put there.
 *
 * The predicate is [Bookmark.aiTags] being non-empty — the *result* — and deliberately not
 * [Bookmark.aiState]. The two disagree, and the disagreement is the point: an enrichment
 * that finished on a page the model had nothing to say about leaves `aiState = "done"` with
 * an empty `aiTags`, and *"has this row got AI tags?"* is answered by the tags, not by
 * whether a worker finished.
 *
 * A knob over the queue itself — pending / running / failed, to find rows that gave up — is
 * a different question on a different axis, and [BookmarkQuery.aiStates] is what answers it.
 * Do not conflate the two; reaching for [AiStates] here is the easy mistake, because it is
 * the more prominent vocabulary.
 */
object AiTagFilters {
    /** Say nothing about it. The default. */
    const val ANY: String = "any"

    /** Keep only rows the model put at least one tag on. */
    const val HAS: String = "has"

    /** Keep only rows it put none on — including the ones it looked at and had none for. */
    const val MISSING: String = "missing"

    val all: List<String> = listOf(ANY, HAS, MISSING)

    /**
     * Whether [bookmark] survives this knob at [value].
     *
     * An unknown [value] is [ANY], for the reason the vocabularies above give: a later
     * client's fourth state must degrade to "no opinion" rather than to an empty window.
     */
    fun keeps(bookmark: Bookmark, value: String): Boolean = when (value) {
        HAS -> bookmark.aiTags.isNotEmpty()
        MISSING -> bookmark.aiTags.isEmpty()
        else -> true
    }
}

/**
 * Whether a bookmark carries a summary the model wrote.
 *
 * Reads [Bookmark.aiSummary] rather than [Bookmark.aiState], for the reason [AiTagFilters]
 * gives. The user's own [Bookmark.summary] never satisfies this knob — that is what
 * [SummaryPresences] is for, and the two are separate on purpose: this one asks whether the
 * *machine* got to a row, and folding it into the four-state question about both summaries
 * would lose the distinction the collection is curated around.
 */
object AiSummaryFilters {
    /** Say nothing about it. The default. */
    const val ANY: String = "any"

    /** Keep only rows the model wrote a summary for. */
    const val HAS: String = "has"

    /** Keep only rows it did not — never tried, gave up, or had nothing to say. */
    const val MISSING: String = "missing"

    val all: List<String> = listOf(ANY, HAS, MISSING)

    /** Whether [bookmark] survives this knob at [value]. Unknown values are [ANY]. */
    fun keeps(bookmark: Bookmark, value: String): Boolean = when (value) {
        HAS -> bookmark.aiSummary.isNotBlank()
        MISSING -> bookmark.aiSummary.isBlank()
        else -> true
    }
}

/** How [BookmarkQuery.host] is compared. */
object HostMatches {
    /**
     * The host, or any subdomain of it. The default, and the useful one: somebody asking
     * about `github.com` means `gist.github.com` too.
     */
    const val SUFFIX: String = "suffix"

    /** Exactly this host and nothing under it. */
    const val EXACT: String = "exact"

    val all: List<String> = listOf(SUFFIX, EXACT)
}

/**
 * How many rows a bounded read hands back when nobody said.
 *
 * Fifty, which is well under any collection worth filtering. The number is here rather
 * than at the call site because it is a promise made in a tool description — *reading is
 * cheap by default* — and a default that lived in one caller would be a promise the next
 * caller did not make.
 */
const val DEFAULT_QUERY_LIMIT: Int = 50

/**
 * The most rows one read may hand back, however large a limit is asked for.
 *
 * Five hundred. Past this a caller is not reading a subset, it is pulling the collection
 * through somebody's context window — which is precisely the move `describe_selection`
 * and `count_bookmarks` exist to make unnecessary. Clamped rather than refused: a caller
 * that asked for a thousand and got five hundred plus a `total` saying there were nine
 * hundred knows exactly where it stands.
 */
const val MAX_QUERY_LIMIT: Int = 500

/**
 * One question about a collection.
 *
 * Every field is optional and every default says nothing, so an empty [BookmarkQuery] is
 * "all of them, newest first". That matters more than it looks: it means a caller adds
 * clauses to narrow and never has to name a clause it does not care about, and it means
 * the web app's unfiltered window and an agent's unfiltered search are the same object.
 *
 * @property query free text over the title, the URL, the person's note and the machine's
 *   summary — four fields, one needle, as [matchesText] defines it. Case-insensitive,
 *   substring rather than word matching, because that is what the search field has always
 *   done and a second meaning of "matching" is exactly what this file exists to prevent.
 * @property includeTags tags a bookmark must carry, combined by [match].
 * @property excludeTags tags that disqualify a bookmark. **Excluding beats including** —
 *   see [filterBookmarks], where the ordering is argued.
 * @property match one of [FilterMatches].
 * @property untagged keep only bookmarks with no tags at all. The one thing tags cannot
 *   express about themselves: there is no tag meaning "none of them". Combined with
 *   [includeTags] it is a contradiction and answers nothing, which is the honest result
 *   rather than an error — the same reading `#a and not #a` gets.
 * @property addedWithin a rolling window, one of [AddedWithins].
 * @property addedAfter epoch millis, inclusive. An absolute floor, for the questions a
 *   filter bar cannot ask: *what did I keep in 2019*. Applied alongside [addedWithin]
 *   rather than instead of it — two bounds narrow, they do not override each other.
 * @property addedBefore epoch millis, inclusive.
 * @property aiStates which of [AiStates] to keep. Empty says nothing. **[AiStates.FAILED]
 *   is a first-class thing to ask for** and is the reason this field exists: "show me
 *   everything the summariser gave up on" had no expression anywhere in LunaPin before
 *   this, and it is what the batch enrichment tools are pointed at.
 * @property hasSummary one of [SummaryPresences].
 * @property host a URL host, compared by [hostMatch]. Normalised the way [hostOf]
 *   normalises a bookmark's own: lower-cased and without a leading `www.`, so
 *   `WWW.GitHub.com` and `github.com` are one question.
 * @property hostMatch one of [HostMatches].
 * @property sort one of [BookmarkSorts].
 * @property limit how many rows a paged read returns. Clamped to [MAX_QUERY_LIMIT] by
 *   [pageOf]; ignored entirely by [filterBookmarks], which answers the whole match.
 * @property offset how many matched rows to skip before [limit] is taken. Paging over a
 *   total order — every comparator in [sortBookmarks] ends in the id — so two pages of one
 *   query cannot overlap or skip a row.
 */
@Serializable
data class BookmarkQuery(
    val query: String = "",
    val includeTags: List<String> = emptyList(),
    val excludeTags: List<String> = emptyList(),
    val match: String = FilterMatches.ALL,
    val untagged: Boolean = false,
    val addedWithin: String = AddedWithins.ANY,
    val addedAfter: Long? = null,
    val addedBefore: Long? = null,
    val aiStates: List<String> = emptyList(),
    val hasSummary: String = SummaryPresences.ANY,
    val aiTags: String = AiTagFilters.ANY,
    val aiSummary: String = AiSummaryFilters.ANY,
    val host: String = "",
    val hostMatch: String = HostMatches.SUFFIX,
    val sort: String = BookmarkSorts.NEWEST,
    val limit: Int = DEFAULT_QUERY_LIMIT,
    val offset: Int = 0,
)

/**
 * The rows [query] keeps, in [BookmarkQuery.sort] order. **All of them** — see the file
 * header on why the page is a second step.
 *
 * **Excluding beats including.** A bookmark carrying an excluded tag is gone whatever else
 * it carries, including a tag that is also included — which is the only rule that makes
 * the web rail's third chip state mean anything: "everything in `#read-later` except the
 * `#gaming` ones" is the sentence people actually filter with, and it requires the two
 * halves to be ordered rather than merged.
 *
 * @param bookmarks the collection, in whatever order it arrived.
 * @param query what to keep.
 * @param now epoch millis, for [BookmarkQuery.addedWithin]. Passed rather than read,
 *   because shared code has no clock it can reach without an `expect` declaration — and
 *   because a function given the time is a function a test can pin.
 */
fun filterBookmarks(
    bookmarks: List<Bookmark>,
    query: BookmarkQuery,
    now: Long,
): List<Bookmark> {
    val excluded = query.excludeTags.mapTo(mutableSetOf()) { normalisedTag(it) }
    val included = query.includeTags.map { normalisedTag(it) }.filter { it.isNotEmpty() }
    val needle = query.query.trim().lowercase()
    val since = AddedWithins.millis(query.addedWithin)?.let { now - it }
    val states = query.aiStates.mapTo(mutableSetOf()) { it.trim().lowercase() }.filter { it.isNotEmpty() }
    val wantedHost = normalisedHost(query.host)
    // Whether the tag halves have anything to say at all, asked once rather than per
    // bookmark: with both empty — the ordinary case, a plain text search — the whole tag
    // step is skipped and no `Set` is built for a collection's worth of rows.
    val readsTags = excluded.isNotEmpty() || included.isNotEmpty()
    val matchAny = query.match == FilterMatches.ANY
    // ── One pass, and it is not merely tidiness ──────────────────────────────
    //
    // This was eleven chained `.filter` calls, which is eleven intermediate lists of a
    // shrinking collection allocated on every keystroke in a search field. Every step is a
    // conjunct — order among them is immaterial, as the AI knobs' note below says — so
    // they compose into one predicate that answers `false` at the first thing that fails.
    // The cheap tests lead and the text scan trails, because the text scan is the only one
    // that reads a paragraph.
    return bookmarks
        .filter { bookmark ->
            if (readsTags) {
                val tags = if (bookmark.tags.size > LINEAR_TAG_SCAN) bookmark.tags.toSet() else bookmark.tags
                when {
                    excluded.any { it in tags } -> return@filter false
                    included.isEmpty() -> Unit
                    matchAny -> if (included.none { it in tags }) return@filter false
                    else -> if (!included.all { it in tags }) return@filter false
                }
            }
            if (query.untagged && bookmark.tags.isNotEmpty()) return@filter false
            if (since != null && bookmark.createdAt < since) return@filter false
            if (query.addedAfter != null && bookmark.createdAt < query.addedAfter) return@filter false
            if (query.addedBefore != null && bookmark.createdAt > query.addedBefore) return@filter false
            if (states.isNotEmpty() && bookmark.aiState !in states) return@filter false
            if (!bookmark.hasSummaryOf(query.hasSummary)) return@filter false
            // The two AI knobs, which keep and drop rather than order. They read the
            // results — the tags and the summary the model left — not the queue state above
            // them, and they compose with each other and with everything else here.
            if (!AiTagFilters.keeps(bookmark, query.aiTags)) return@filter false
            if (!AiSummaryFilters.keeps(bookmark, query.aiSummary)) return@filter false
            if (wantedHost.isNotEmpty() &&
                !hostKeyOf(bookmark.url).matchesHost(wantedHost, query.hostMatch)
            ) {
                return@filter false
            }
            needle.isEmpty() || bookmark.matchesText(needle)
        }
        .let { sortBookmarks(it, query.sort) }
}

/**
 * Past how many tags on one bookmark it is worth building a `Set` to ask about them.
 *
 * A bookmark carries a handful of tags, and a linear scan of a handful beats allocating a
 * hash set to scan it once. The threshold is where the two cross in practice rather than a
 * measured constant — being wrong either way costs a few nanoseconds on a row, and being
 * wrong in the old direction cost a set allocation per row per keystroke.
 */
private const val LINEAR_TAG_SCAN = 16

/**
 * The slice of [matched] that [query] asked for, with the limit clamped.
 *
 * Separate from [filterBookmarks] so that a caller can report `total` — the count *before*
 * the cut — from the same traversal. That number is the whole of what makes a bounded read
 * honest: "50 rows, of 340" is a result an agent narrows from, while "50 rows" is one it
 * quietly believes is everything.
 *
 * An offset past the end is an empty page rather than an error, which is what every paging
 * caller wants at the end of a list.
 */
fun pageOf(matched: List<Bookmark>, query: BookmarkQuery): List<Bookmark> {
    val limit = query.limit.coerceIn(1, MAX_QUERY_LIMIT)
    val offset = query.offset.coerceAtLeast(0)
    if (offset >= matched.size) return emptyList()
    return matched.subList(offset, minOf(offset + limit, matched.size))
}

/**
 * Whether the free text is anywhere in this bookmark's words.
 *
 * Title, URL and the summary — which is **both** summaries, the user's note and the
 * model's. The web search field's standing reminder says `TITLE · URL · SUMMARY` and that
 * is three things a reader can name; splitting the last one into two would be describing
 * the schema rather than the row, and a person searching for a phrase they remember
 * reading does not know which half of the row it was in.
 *
 * `internal` so [filterBookmarks] is the only door: a second caller matching text its own
 * way would be a second definition of "matching", which is the thing this file is for.
 *
 * ── Why both sides are lower-cased rather than `ignoreCase = true` ───────────
 *
 * This is the hottest line in the app and the difference is not a constant factor worth
 * shrugging at. `contains(needle, ignoreCase = true)` cannot use the platform's own
 * substring search: the standard library falls back to walking every start position and
 * comparing character by character, converting the case of each pair as it goes. Over four
 * fields of a few thousand bookmarks — and `aiSummary` is a paragraph — that is around
 * **170 ms per keystroke** in a browser, which is what made typing in the search field feel
 * broken on a large collection.
 *
 * Lower-casing the haystack once and asking a case-sensitive `contains` hands the search to
 * the platform's own `indexOf`, and the same measurement comes out around **1 ms**. The
 * answer is identical — the needle arrives lower-cased, so a lower-cased haystack is exactly
 * what `ignoreCase` was approximating — for every alphabet whose case folding is a
 * per-character mapping, which is what the character-by-character path was doing anyway.
 *
 * The fields are asked one at a time, shortest first, so the common case of a word in a
 * title never touches the model's summary at all.
 *
 * @param needle already trimmed and lower-cased by the caller, so this is not re-done per
 *   bookmark over a whole collection.
 */
internal fun Bookmark.matchesText(needle: String): Boolean =
    title.lowercase().contains(needle) ||
        url.lowercase().contains(needle) ||
        summary.lowercase().contains(needle) ||
        aiSummary.lowercase().contains(needle)

/** Whether this bookmark satisfies one of [SummaryPresences]. Unknown values say nothing. */
private fun Bookmark.hasSummaryOf(presence: String): Boolean = when (presence) {
    SummaryPresences.WRITTEN -> summary.isNotBlank()
    SummaryPresences.AI -> aiSummary.isNotBlank()
    SummaryPresences.EITHER -> summary.isNotBlank() || aiSummary.isNotBlank()
    SummaryPresences.NEITHER -> summary.isBlank() && aiSummary.isBlank()
    else -> true
}

/** Whether this host answers a question about [wanted]. Unknown match modes are [HostMatches.SUFFIX]. */
private fun String.matchesHost(wanted: String, mode: String): Boolean = when (mode) {
    HostMatches.EXACT -> this == wanted
    else -> this == wanted || endsWith(".$wanted")
}

/**
 * [bookmarks] in [sort] order.
 *
 * Every comparator ends in the id, so the order is **total**: two bookmarks saved in the
 * same millisecond, or two with the same title, must not swap places between two repaints
 * of the same list — nor between two pages of one query, which is the property [pageOf]
 * rests on. An unrecognised sort is [BookmarkSorts.NEWEST], which is the server's own
 * order and therefore the one that costs nothing to be wrong about.
 */
fun sortBookmarks(bookmarks: List<Bookmark>, sort: String): List<Bookmark> = when (sort) {
    BookmarkSorts.OLDEST -> bookmarks.sortedWith(compareBy({ it.createdAt }, { it.id }))
    // Decorated, because a comparator is called O(n log n) times and [sortTitle] allocates:
    // sorting a few thousand rows by title through the comparator lower-cased every title
    // some seventy times over. The key is computed once per row and thrown away with the
    // pairs. The order is the one the comparator gave — key, then id.
    BookmarkSorts.TITLE -> bookmarks
        .map { it.sortTitle() to it }
        .sortedWith(compareBy({ it.first }, { it.second.id }))
        .map { it.second }

    else -> bookmarks.sortedWith(compareByDescending<Bookmark> { it.createdAt }.thenByDescending { it.id })
}

/** What A–Z sorts on: the title, or the URL for a bookmark that never got one. */
private fun Bookmark.sortTitle(): String = title.ifBlank { url }.lowercase()

/**
 * The host of [url] — what sits between the scheme and the first slash, without `www.`.
 *
 * Hand-rolled rather than through a URL parser, because that is the only part wanted and
 * every platform's parser disagrees about the rest — and because there is no URL parser in
 * `commonMain` at all.
 *
 * **This is a display function**, and its behaviour is byte-for-byte what `:client`'s
 * `hostOf` did before this file existed: a bookmark row, a sync-log line and a workspace
 * miniature all draw a host under a title through it, and three surfaces disagreeing about
 * one collection is what it was written to prevent. It deliberately does *not* fold case
 * or strip a port, because those are things a reader can see in the URL and would notice
 * being changed. Matching a host is [hostKeyOf]'s job, and it is a different question.
 */
fun hostOf(url: String): String =
    url.substringAfter("://", url).substringBefore('/').removePrefix("www.")

/**
 * The host of [url] as a **key**: lower-cased, no port, no credentials, no query.
 *
 * What [BookmarkQuery.host] is compared against, and separate from [hostOf] because the
 * two answer different questions. A reader wants to see what the URL says; a filter wants
 * `HTTPS://WWW.GitHub.com:443/x` and `https://github.com/y` to be one site — otherwise
 * "everything from github.com" quietly misses rows for reasons nobody can see.
 */
fun hostKeyOf(url: String): String {
    val trimmed = url.trim()
    // The authority, whether or not a scheme was written: an agent asking about
    // "github.com" sends no scheme, and a stored URL always has one.
    val authority = trimmed.substringAfter("://", trimmed)
        .substringBefore('/')
        .substringBefore('?')
        .substringBefore('#')
    // Case last, so `WWW.` is stripped as reliably as `www.` — the folding has to happen
    // before the prefix is looked for, not after.
    return authority.substringAfterLast('@').substringBefore(':').lowercase().removePrefix("www.")
}

/** A host as this file compares them. See [hostKeyOf]. */
private fun normalisedHost(host: String): String = hostKeyOf(host)

/**
 * One tag, as the server stores it: trimmed, lower-cased, with exactly one leading `#`.
 *
 * A second implementation of `BookmarkStore.hashPrefixed`, and the duplication is the
 * point of being explicit about it: the server's copy is what *writes* the column and
 * cannot live in `commonMain` without dragging the store's other rules with it, while
 * this one is what lets a filter written on either side compare like with like. They are
 * three lines and they are asserted against each other in the shared tests.
 */
private fun normalisedTag(tag: String): String {
    val bare = tag.trim().trimStart('#').trim().lowercase()
    return if (bare.isEmpty()) "" else "#$bare"
}
